import { PropsWithChildren, useEffect, useRef } from 'react';
import { logError } from '../utils/logging';
import { debounce } from 'tiny-throttle';
import classNames from 'classnames';
import isMobile from '../common/utils/isMobile';

type ContentProps = PropsWithChildren<{
  contentHeight: number;
  setContentHeight: (height: number) => void;
  className?: string;
  isDiscountEnabled: boolean | undefined;
}>;

export function Content({
  children,
  contentHeight,
  setContentHeight,
  className,
  isDiscountEnabled,
}: ContentProps) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  function callbackChangeHeight() {
    const height =
      (contentRef && contentRef.current && contentRef.current.offsetHeight) ??
      0;
    if (height !== contentHeight) {
      setContentHeight(height);
      contentHeight = height;
    }
  }

  useEffect(() => {
    if (!contentRef.current) {
      return;
    }
    try {
      // eslint-disable-next-line
      const observer = new MutationObserver(
        debounce(callbackChangeHeight, 100),
      );
      observer.observe(contentRef.current, {
        childList: true,
        subtree: true,
      });
      callbackChangeHeight();
      return () => observer.disconnect();
    } catch (error) {
      logError('Failed to setup content observer', error);
    }
  }, [setContentHeight, contentRef]);

  return (
    <div
      className={classNames(
        'GiftBox-Content-Wrapper flex flex-col justify-start py-6 px-5',
        { 'pb-20': isDiscountEnabled, 'pt-2': isMobile() },
        `${className ?? ''}`,
      )}
      ref={contentRef}
    >
      {children}
    </div>
  );
}
